/**
 * Client delegations for the resumable upload surface (`/api/uploads/*`).
 * In prod the bytes go DIRECT to GCS (UpChunk); in local-storage dev there's no
 * GCS to PUT to, so the bytes go to `/uploads/:id/bytes` (`uploadLocalBytes`).
 * `reserve`/`finalize` are the same in both modes.
 */

import type {
  ApiResponse,
  UploadFinalizeResponse,
  UploadReserveRequest,
  UploadReserveResponse,
  UploadStatusResponse,
} from '@bower/shared';

import type { HttpClient } from './http-client.types';

export function reserveUpload(http: HttpClient, workspaceId: string, body: UploadReserveRequest) {
  return http.request({
    method: 'POST',
    url: '/uploads/reserve',
    data: body,
    headers: { 'x-workspace-id': workspaceId },
  }) as Promise<ApiResponse<UploadReserveResponse>>;
}

export function finalizeUpload(http: HttpClient, workspaceId: string, uploadId: string) {
  return http.request({
    method: 'POST',
    url: `/uploads/${uploadId}/finalize`,
    headers: { 'x-workspace-id': workspaceId },
  }) as Promise<ApiResponse<UploadFinalizeResponse>>;
}

export function getUploadStatus(
  http: HttpClient,
  workspaceId: string,
  uploadId: string,
  options?: { readonly signal?: AbortSignal; readonly timeoutMs?: number },
) {
  return http.request(
    {
      method: 'GET',
      url: `/uploads/${uploadId}/status`,
      headers: { 'x-workspace-id': workspaceId },
      ...(options?.signal ? { signal: options.signal } : {}),
      ...(options?.timeoutMs !== undefined ? { timeout: options.timeoutMs } : {}),
    },
    0,
  ) as Promise<ApiResponse<UploadStatusResponse>>;
}

/**
 * Local-dev byte transport (no GCS). PUTs the raw file body to the backend,
 * which streams it to local storage. Only used when `reserve` returned a
 * `local:` session sentinel. Sent as `application/octet-stream` ALWAYS — never
 * the file's real type — so the JSON body parser can't match + consume the
 * stream (a `.json`/`.xml` upload would otherwise arrive empty). The real mime
 * lives in the server-side upload sidecar from `reserve`.
 */
export function uploadLocalBytes(
  http: HttpClient,
  workspaceId: string,
  uploadId: string,
  file: File,
  options?: {
    onProgress?: (pct: number) => void;
    signal?: AbortSignal;
  },
) {
  return http.request({
    method: 'PUT',
    url: `/uploads/${uploadId}/bytes`,
    data: file,
    headers: {
      'Content-Type': 'application/octet-stream',
      'x-workspace-id': workspaceId,
    },
    onUploadProgress: (e: { loaded: number; total?: number }) => {
      if (options?.onProgress && e.total) {
        options.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
    ...(options?.signal ? { signal: options.signal } : {}),
  }) as Promise<ApiResponse<unknown>>;
}
